import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  NZ_MODAL_DATA,
  NzModalModule,
  NzModalRef,
  NzModalService,
} from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  Bank,
  CustomerService,
  PackagePaymentMethod,
  ServicePackage,
  ServicePackageListResponse,
  ServicePackageService,
  ServiceService,
} from '@beautyapp/core';
import {
  buildResetPatch,
  paymentSumMismatch,
} from '@beautyapp/service-packages';
import {
  InputComponent,
  SelectComponent,
  SelectSearchComponent,
} from '@beautyapp/form';
import { AddUserComponent } from '@beautyapp/shared';
import {
  rxResource,
  takeUntilDestroyed,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  catchError,
  debounceTime,
  defaultIfEmpty,
  distinctUntilChanged,
  finalize,
  tap,
} from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Data passed into the dialog via `NzModalService.create({ nzData })`.
 *
 * `userId` is required — the dialog cannot sell a package without knowing
 * which customer to attach it to. The caller (booking form) always has
 * this from the booking context, so the dialog asserts it on init rather
 * than supporting a "pick customer" sub-flow.
 */
export interface PackageSellDialogData {
  userId: string;
}

/**
 * Result returned via `NzModalRef.close(...)`.
 * `purchaseId` is the new purchase row's id. The caller uses it as a
 * truthy signal to know a purchase happened and to refresh the user's
 * package balance. `null` means the operator cancelled.
 */
export type PackageSellDialogResult = { purchaseId: string } | null;

// Mirrors sell-package-modal.paymentMethods (libs/service-packages/.../sell-package-modal).
// Keep in sync so the in-booking shortcut accepts the same methods as the full
// /service-packages admin page — including MIXED/CONSIGNATION which are handled
// uniformly by the backend's sanitizePaymentFields helper post-#2890–2894.
const ALL_PAYMENT_METHODS: { value: PackagePaymentMethod; labelKey: string }[] =
  [
    { value: 'CASH', labelKey: 'PAYMENTTYPE.1' },
    { value: 'CARD', labelKey: 'PAYMENTTYPE.2' },
    { value: 'MIXED', labelKey: 'PAYMENTTYPE.9' },
    { value: 'BALANCE', labelKey: 'PAYMENTTYPE.5' },
    { value: 'BARTER', labelKey: 'PAYMENTTYPE.6' },
    { value: 'CONSIGNATION', labelKey: 'PAYMENTTYPE.7' },
    { value: 'BANK_TRANSFER', labelKey: 'PAYMENTTYPE.8' },
  ];

const EMPTY_PAGE: ServicePackageListResponse = {
  data: [],
  meta: { page: 1, pageSize: 10, pageCount: 0, itemCount: 0 },
};

@Component({
  selector: 'beautyapp-package-sell-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzModalModule,
    NzButtonModule,
    NzTableModule,
    NzInputModule,
    NzInputNumberModule,
    NzSelectModule,
    NzFormModule,
    NzSpinModule,
    NzDividerModule,
    NzTagModule,
    NzAlertModule,
    NzSegmentedModule,
    NzIconModule,
    NzCheckboxModule,
    InputComponent,
    SelectComponent,
    SelectSearchComponent,
  ],
  templateUrl: './package-sell-dialog.component.html',
})
export class PackageSellDialogComponent {
  private readonly packageService = inject(ServicePackageService);
  private readonly serviceService = inject(ServiceService);
  private readonly customerService = inject(CustomerService);
  private readonly modalService = inject(NzModalService);
  private readonly modalRef =
    inject<NzModalRef<PackageSellDialogComponent, PackageSellDialogResult>>(
      NzModalRef
    );
  private readonly data = inject<PackageSellDialogData>(NZ_MODAL_DATA);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);

  // ============ SEARCH + PAGINATION ============

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  readonly page = signal(1);
  readonly pageSize = signal(10);

  readonly searchTerm = toSignal(
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.page.set(1))
    ),
    { initialValue: '' }
  );

  readonly packagesResource = rxResource<
    ServicePackageListResponse,
    { q: string; page: number; pageSize: number }
  >({
    params: () => ({
      q: this.searchTerm() ?? '',
      page: this.page(),
      pageSize: this.pageSize(),
    }),
    stream: ({ params }) =>
      this.packageService
        .getAll({
          isActive: 'true',
          q: params.q.trim() || undefined,
          page: params.page,
          pageSize: params.pageSize,
        })
        .pipe(
          defaultIfEmpty(EMPTY_PAGE),
          catchError(() => of(EMPTY_PAGE))
        ),
  });

  readonly packages = computed(() => this.packagesResource.value()?.data ?? []);
  readonly total = computed(
    () => this.packagesResource.value()?.meta?.itemCount ?? 0
  );
  readonly loadingPackages = computed(() => this.packagesResource.isLoading());
  readonly hasSearchTerm = computed(
    () => (this.searchTerm() ?? '').trim().length > 0
  );

  // ============ SELL FORM ============

  readonly selected = signal<ServicePackage | null>(null);
  readonly submitting = signal(false);

  // ============ GIFT (buy for another customer) — beaut-251 ============
  // The visit customer (data.userId) is the payer; when gift is on, a separate
  // beneficiary consumes the package. Mirrors the admin modal's customer search.
  readonly isGift = signal(false);
  readonly beneficiaries = signal<Array<{ id: string; name: string }>>([]);
  readonly beneficiaryId = new FormControl<string | null>(null);

  toggleGift(on: boolean): void {
    this.isGift.set(on);
    this.beneficiaryId.setValidators(on ? [Validators.required] : []);
    if (!on) this.beneficiaryId.setValue(null);
    this.beneficiaryId.updateValueAndValidity();
  }

  onBeneficiarySearch(term: string): void {
    if (!term || term.length < 2) {
      this.beneficiaries.set([]);
      return;
    }
    this.customerService.search(term).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (users: any[]) =>
        this.beneficiaries.set(
          (users || []).map((u: any) => ({
            id: u.id,
            name: u.fullName + (u.mobilePhone ? ` - ${u.mobilePhone}` : ''),
          }))
        ),
      error: () => this.beneficiaries.set([]),
    });
  }

  // beaut-251 QA: the beneficiary may not exist yet as a salon customer — let the
  // operator create one inline (mirrors the admin modal's add-user flow).
  addNewBeneficiary(): void {
    const modal = this.modalService.create({
      nzTitle: this.translate.instant('calendar.createUser'),
      nzContent: AddUserComponent,
      nzWidth: '900px',
      nzStyle: { top: '0px' },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modal.afterClose.subscribe((res: any) => {
      if (!res?.id) return;
      const created = {
        id: res.id,
        name: res.fullName + (res.mobilePhone ? ` - ${res.mobilePhone}` : ''),
      };
      this.beneficiaries.set([created, ...this.beneficiaries()]);
      this.beneficiaryId.setValue(res.id);
    });
  }

  // ============ SAVE AS TEMPLATE (beaut-362 Variant 2) ============
  // Optionally persist the ad-hoc bundle as a reusable catalog package template,
  // in addition to selling it to this customer.
  readonly saveAsTemplate = signal(false);
  readonly templateName = new FormControl<string>('', { nonNullable: true });

  toggleSaveAsTemplate(on: boolean): void {
    this.saveAsTemplate.set(on);
    this.templateName.setValidators(on ? [Validators.required] : []);
    if (!on) this.templateName.setValue('');
    this.templateName.updateValueAndValidity();
  }

  // Map the current ad-hoc bundle to a CreateServicePackageDto and persist it as
  // a catalog template (best-effort — the sale is authoritative). beaut-362.
  private saveBundleAsTemplate(): void {
    this.packageService
      .create({
        name: this.templateName.value.trim(),
        validityDays: this.validityDays.value,
        isActive: true,
        items: this.bundle.getRawValue().map((r) => ({
          serviceId: r.serviceId as string,
          quantity: r.quantity as number,
          originalPrice: r.originalPrice as number,
          discountedPrice: r.discountedPrice as number,
        })),
      })
      .subscribe({
        next: () =>
          this.message.success(
            this.translate.instant('services.packages.templateSaved')
          ),
        error: () =>
          this.message.warning(
            this.translate.instant('services.packages.templateSaveFailed')
          ),
      });
  }

  // Customer's wallet balance, shown next to BALANCE so the operator can see
  // whether it covers the package price before submitting. Fetched once on
  // init since userId is fixed for the booking.
  readonly selectedBalance = signal<number | null>(null);

  readonly banks: { id: Bank; name: string }[] = [
    { id: Bank.TBC, name: 'TBC bank' },
    { id: Bank.BOG, name: 'Bank of Georgia' },
    { id: Bank.Liberty, name: 'Liberty' },
    { id: Bank.Other, name: 'Other' },
  ];

  readonly paymentMethods = ALL_PAYMENT_METHODS.map((m) => ({
    value: m.value,
    label: this.translate.instant(m.labelKey),
  }));

  readonly form = new FormGroup({
    paymentMethod: new FormControl<PackagePaymentMethod>('CASH', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    cash: new FormControl<number | null>(null),
    card: new FormControl<number | null>(null),
    balance: new FormControl<number | null>(null),
    bank: new FormControl<Bank | null>(null),
    bankTransfer: new FormControl<number | null>(null),
    bankTransferBank: new FormControl<Bank | null>(null),
    consignation: new FormControl<number | null>(null),
    comment: new FormControl<string>('', { nonNullable: true }),
  });

  // ============ AD-HOC MODE (beaut-367) ============

  // 'template' = sell a saved package (search + select). 'adHoc' = build a
  // bundle of procedures on the fly with no template. Default = current flow.
  readonly mode = signal<'template' | 'adHoc'>('template');
  readonly modeOptions = [
    {
      label: this.translate.instant('services.packages.fromTemplate'),
      value: 'template',
    },
    {
      label: this.translate.instant('services.packages.adHoc'),
      value: 'adHoc',
    },
  ];

  // Procedure catalog for the bundle picker — same source the package form uses.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly services = signal<any[]>([]);

  // Set while patching a row programmatically (procedure select), so the input
  // change handlers don't fire and re-sync the discount. Mirrors package-form.
  private suppressBundleSync = false;

  readonly bundle = new FormArray<
    FormGroup<{
      serviceId: FormControl<string | null>;
      quantity: FormControl<number>;
      originalPrice: FormControl<number>;
      discountPercent: FormControl<number>;
      discountedPrice: FormControl<number>;
    }>
  >([]);
  readonly validityDays = new FormControl<number>(30, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(1)],
  });

  private readonly bundleValue = toSignal(this.bundle.valueChanges, {
    initialValue: this.bundle.value,
  });
  private readonly bundleStatus = toSignal(this.bundle.statusChanges, {
    initialValue: this.bundle.status,
  });
  private readonly validityStatus = toSignal(this.validityDays.statusChanges, {
    initialValue: this.validityDays.status,
  });

  // Effective per-unit price: the discounted price when a real discount is set,
  // otherwise the original (discountedPrice=0 means "no discount", not free).
  private effectiveUnitPrice(r: {
    originalPrice?: number;
    discountedPrice?: number;
  }): number {
    const original = Number(r?.originalPrice) || 0;
    const discounted = Number(r?.discountedPrice) || 0;
    return discounted > 0 ? discounted : original;
  }

  // Bundle total = Σ effective price × quantity. Drives the payment validator
  // and the headline total. The pre-discount total is shown when it differs.
  readonly adHocTotal = computed(() =>
    (this.bundleValue() ?? []).reduce(
      (acc, r) => acc + this.effectiveUnitPrice(r) * (Number(r?.quantity) || 0),
      0
    )
  );
  readonly adHocOriginalTotal = computed(() =>
    (this.bundleValue() ?? []).reduce(
      (acc, r) =>
        acc + (Number(r?.originalPrice) || 0) * (Number(r?.quantity) || 0),
      0
    )
  );
  readonly adHocHasDiscount = computed(
    () => this.adHocOriginalTotal() > this.adHocTotal()
  );

  // Show the payment section once there is something to pay for: a selected
  // template, or at least one bundle row in ad-hoc mode.
  readonly showPayment = computed(() =>
    this.mode() === 'adHoc'
      ? (this.bundleValue()?.length ?? 0) > 0
      : !!this.selected()
  );

  // beaut-219: for amount methods the entered channels must sum to the price —
  // the booking shortcut previously skipped this, letting a package sell for the
  // wrong amount. Shared with the admin sell-package-modal via the
  // paymentSumMismatch helper so the two can never drift again. In ad-hoc mode
  // the target is the bundle total (effectivePrice) rather than a template price.
  private readonly paymentSumValidator = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const v = group.getRawValue();
    const mismatch = paymentSumMismatch(
      v.paymentMethod,
      this.effectivePrice,
      v
    );
    return mismatch ? { paymentSumMismatch: mismatch } : null;
  };

  // Reactive mirror of form validity so the submit button disables on mismatch.
  private readonly formStatus = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  readonly canSubmit = computed(() => {
    if (this.submitting() || this.formStatus() !== 'VALID') return false;
    if (this.mode() === 'adHoc') {
      return (
        (this.bundleValue()?.length ?? 0) > 0 &&
        this.bundleStatus() === 'VALID' &&
        this.validityStatus() === 'VALID'
      );
    }
    return !!this.selected();
  });

  constructor() {
    // Enforce sum-equals-price on every value/method change.
    this.form.addValidators(this.paymentSumValidator);
    this.form.updateValueAndValidity({ emitEvent: false });

    // Reset channel fields on every payment-method change. Without this, values
    // from a previously selected method leak into the POST payload and trigger
    // backend @Min(0) false-positives. Mirrors sell-package-modal exactly so
    // the in-booking shortcut behaves identically to the admin sell flow.
    this.form.controls.paymentMethod.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((method) => {
        if (!method) return;
        // Template mode needs a selected package before there's a price to fill;
        // ad-hoc mode fills from the bundle total (effectivePrice) instead.
        if (this.mode() === 'template' && !this.selected()) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.form.patchValue(
          buildResetPatch(method, this.effectivePrice) as any
        );
      });

    // Ad-hoc: when the bundle changes, keep the 'auto' channel (e.g. CASH/BALANCE
    // display) tracking the new total, then force-revalidate the payment form.
    // The sum validator depends on effectivePrice (an external input), so it
    // won't re-run on its own — and for methods whose reset patch is a no-op
    // (MIXED/CARD, user-entered channels) patchValue alone wouldn't trigger it.
    this.bundle.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.mode() !== 'adHoc') return;
      const method = this.form.controls.paymentMethod.value;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.form.patchValue(
        buildResetPatch(method, this.effectivePrice) as any,
        {
          emitEvent: false,
        }
      );
      this.form.updateValueAndValidity();
    });

    // Load the procedure catalog for the ad-hoc picker (same source as the
    // package form). Drop the empty/undefined sentinel service (beaut-300).
    this.serviceService.dropdown().subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (services: any[]) =>
        this.services.set((services || []).filter((s) => s && s.id)),
      error: () => this.services.set([]),
    });

    // Fetch the booking customer's wallet balance once. Failures are silent —
    // the BALANCE preview just stays hidden so the operator falls back to
    // submitting and reading the backend's insufficient-balance error.
    this.customerService.getById(this.data.userId).subscribe({
      next: (user) => {
        if (user && user.balance !== undefined && user.balance !== null) {
          this.selectedBalance.set(Number(user.balance));
        }
      },
      error: () => {
        this.selectedBalance.set(null);
      },
    });
  }

  get selectedMethod(): PackagePaymentMethod | null {
    return this.form.get('paymentMethod')?.value ?? null;
  }

  // The wallet balance only funds the sale for these methods, so it should only
  // read as insufficient (red) then — not when paying by cash/card/etc.
  get balanceRelevant(): boolean {
    return this.selectedMethod === 'BALANCE' || this.selectedMethod === 'MIXED';
  }

  get packagePrice(): number {
    const pkg = this.selected();
    return pkg ? Number(pkg.price) : 0;
  }

  // The price the payment channels must match: the bundle total in ad-hoc mode,
  // the selected package price in template mode.
  get effectivePrice(): number {
    return this.mode() === 'adHoc' ? this.adHocTotal() : this.packagePrice;
  }

  onModeChange(mode: 'template' | 'adHoc'): void {
    this.mode.set(mode);
    // The effective price changed with the mode, so re-sync the payment channels
    // and force-revalidate (patchValue alone may be a no-op for some methods).
    const method = this.form.controls.paymentMethod.value;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.form.patchValue(buildResetPatch(method, this.effectivePrice) as any, {
      emitEvent: false,
    });
    this.form.updateValueAndValidity();
  }

  addBundleRow(): void {
    this.bundle.push(
      new FormGroup({
        serviceId: new FormControl<string | null>(null, Validators.required),
        quantity: new FormControl(1, {
          nonNullable: true,
          validators: [Validators.required, Validators.min(1)],
        }),
        originalPrice: new FormControl(0, {
          nonNullable: true,
          validators: [Validators.required, Validators.min(0)],
        }),
        discountPercent: new FormControl(0, {
          nonNullable: true,
          validators: [Validators.min(0), Validators.max(100)],
        }),
        discountedPrice: new FormControl(0, {
          nonNullable: true,
          validators: [Validators.min(0)],
        }),
      })
    );
  }

  removeBundleRow(i: number): void {
    this.bundle.removeAt(i);
  }

  // Prefill the row's original price with the procedure's current price when it's
  // picked (operator can still override); reset any discount. beautyapp-select's
  // (selected) emits the serviceId, so look the service up like package-form does.
  onBundleServiceSelect(i: number, serviceId: string): void {
    if (!serviceId) return;
    const service = this.services().find((s) => s.id === serviceId);
    if (service) {
      this.suppressBundleSync = true;
      this.bundle.at(i).patchValue({
        originalPrice: Number(service.price) || 0,
        discountPercent: 0,
        discountedPrice: 0,
      });
      this.suppressBundleSync = false;
    }
  }

  // Two-way discount sync (mirrors package-form): editing original/percent
  // recomputes the discounted price; editing the discounted price back-computes
  // the percent. discountedPrice=0 stays "no discount", not 100% off.
  onBundleOriginalPriceChange(i: number): void {
    if (this.suppressBundleSync) return;
    const g = this.bundle.at(i);
    const original = +(g.get('originalPrice')?.value || 0);
    const percent = +(g.get('discountPercent')?.value || 0);
    if (percent > 0) {
      g.get('discountedPrice')?.setValue(
        Math.round(original * (1 - percent / 100) * 100) / 100
      );
    }
  }

  onBundleDiscountPercentChange(i: number): void {
    if (this.suppressBundleSync) return;
    const g = this.bundle.at(i);
    const original = +(g.get('originalPrice')?.value || 0);
    const percent = +(g.get('discountPercent')?.value || 0);
    g.get('discountedPrice')?.setValue(
      Math.round(original * (1 - percent / 100) * 100) / 100
    );
  }

  onBundleDiscountedPriceChange(i: number): void {
    if (this.suppressBundleSync) return;
    const g = this.bundle.at(i);
    const original = +(g.get('originalPrice')?.value || 0);
    const discounted = +(g.get('discountedPrice')?.value || 0);
    const percent =
      discounted > 0 && original > 0
        ? Math.round(((original - discounted) / original) * 100)
        : 0;
    g.get('discountPercent')?.setValue(percent);
  }

  percentFormatter = (value: number): string => `${value}%`;
  percentParser = (value: string): number => +value.replace('%', '');

  selectPackage(pkg: ServicePackage): void {
    this.selected.set(pkg);
    // Re-apply the reset patch so the freshly selected package's price flows
    // into the 'auto' channel field for the current payment method.
    const method = this.form.controls.paymentMethod.value;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.form.patchValue(buildResetPatch(method, Number(pkg.price)) as any);
  }

  clearSelection(): void {
    this.selected.set(null);
    this.form.patchValue({
      cash: null,
      card: null,
      balance: null,
      bank: null,
      bankTransfer: null,
      bankTransferBank: null,
      consignation: null,
    });
  }

  onTableChange(event: NzTableQueryParams): void {
    if (event.pageIndex !== this.page()) this.page.set(event.pageIndex);
    if (event.pageSize !== this.pageSize()) this.pageSize.set(event.pageSize);
  }

  private buildPayload(): Record<string, unknown> | null {
    const value = this.form.getRawValue();
    const {
      paymentMethod,
      cash,
      card,
      bank,
      balance,
      bankTransfer,
      bankTransferBank,
      consignation,
      comment,
    } = value;
    if (!paymentMethod) return null;

    const base: Record<string, unknown> = {
      userId: this.data.userId,
      paymentMethod,
    };
    const trimmedComment = comment?.trim();
    if (trimmedComment) base['comment'] = trimmedComment;

    switch (paymentMethod) {
      case 'CASH':
      case 'BARTER':
      case 'BALANCE':
        return base;
      case 'CARD':
        return { ...base, card: card ?? 0, ...(bank ? { bank } : {}) };
      case 'BANK_TRANSFER':
        return {
          ...base,
          bankTransfer: bankTransfer ?? 0,
          ...(bankTransferBank ? { bankTransferBank } : {}),
        };
      case 'MIXED':
        return {
          ...base,
          cash: cash ?? 0,
          card: card ?? 0,
          balance: balance ?? 0,
          bankTransfer: bankTransfer ?? 0,
          ...(bank ? { bank } : {}),
          ...(bankTransferBank ? { bankTransferBank } : {}),
        };
      case 'CONSIGNATION':
        return {
          ...base,
          cash: cash ?? 0,
          card: card ?? 0,
          consignation: consignation ?? 0,
          ...(bank ? { bank } : {}),
        };
      default:
        return base;
    }
  }

  submit(): void {
    if (this.mode() === 'template') {
      if (!this.selected()) return;
    } else if (
      this.bundle.length === 0 ||
      this.bundle.invalid ||
      this.validityDays.invalid
    ) {
      this.bundle.markAllAsTouched();
      this.validityDays.markAsTouched();
      return;
    }

    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });
      return;
    }

    const payload = this.buildPayload();
    if (!payload) return;

    // beaut-251: gift requires a chosen beneficiary; add it to the payload.
    if (this.isGift() && !this.beneficiaryId.value) {
      this.beneficiaryId.markAsTouched();
      return;
    }
    const giftFields =
      this.isGift() && this.beneficiaryId.value
        ? { beneficiaryUserId: this.beneficiaryId.value }
        : {};

    // beaut-362: saving the bundle to templates requires a name.
    if (
      this.mode() === 'adHoc' &&
      this.saveAsTemplate() &&
      !this.templateName.value.trim()
    ) {
      this.templateName.markAsTouched();
      return;
    }

    this.submitting.set(true);
    const request$ =
      this.mode() === 'adHoc'
        ? this.packageService.sellAdHoc({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(payload as any),
            validityDays: this.validityDays.value,
            items: this.bundle.getRawValue().map((r) => ({
              serviceId: r.serviceId as string,
              quantity: r.quantity as number,
              // The price charged per unit is the effective (discounted) price.
              price: this.effectiveUnitPrice(r),
            })),
            ...giftFields,
          })
        : this.packageService.sell(this.selected()!.id, {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(payload as any),
            ...giftFields,
          } as any);

    request$.pipe(finalize(() => this.submitting.set(false))).subscribe({
      next: (purchase) => {
        this.message.success('პაკეტი წარმატებით შეიძინა');
        // beaut-362: also persist the bundle as a reusable catalog template.
        if (
          this.mode() === 'adHoc' &&
          this.saveAsTemplate() &&
          this.templateName.value.trim()
        ) {
          this.saveBundleAsTemplate();
        }
        this.modalRef.close({ purchaseId: purchase.id });
      },
      error: (err) => {
        const detail = err?.error?.message || err?.message || 'შეცდომა';
        this.message.error(`პაკეტის შეძენა ვერ მოხერხდა: ${detail}`);
      },
    });
  }

  cancel(): void {
    this.modalRef.close(null);
  }
}
